import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import TabSelector from '~components/atoms/TabSelector';
import TabDisplay from '~components/atoms/TabDisplay';

import BuyAndSellGoldApproval from './BuyAndSellGoldApproval';

import { ApprovalTabsEnum, ModuleEnum, PermissionsEnum } from '~utils/enums';

import * as Styles from './_approval.module.scss';
import DiscountApproval from './DiscountApproval';
import ProductApproval from './ProductApproval';
import ProjectApproval from './ProjectApproval';
import TransfersApproval from './TransfersApproval';
import CollateralApproval from './CollateralApproval';
import ReleaseEarmarkApproval from './ReleaseEarmarkApproval';
import BranchesApproval from './BranchesApproval';
import GoldConversionApproval from './GoldConversionApproval';
import EarmarkApproval from './EarmarkApproval';
import GoldSilverInventoryApproval from './GoldSilverInventoryApproval';
import StockOutApproval from './StockOutApproval';

const Settings = () => {
    const dispatch = useAppDispatch();

    const selectedTab = useAppSelector(state => state.approval.selectedTab);
    const viewAccessModules = useAppSelector(state => state.auth.userDetails.access).find(access => access.permission === PermissionsEnum.View)?.modules || [];

    useEffect(() => {
        dispatch(combinedActions.authGetIsMarketOpenAttempt());
    }, []);

    const setSelectedTab = (tab: ApprovalTabsEnum) => {
        dispatch(combinedActions.approvalSetSelectedTab(tab));
    };

    viewAccessModules.find(module => module === ModuleEnum.BuySellGold);

    const availableTabValues = [
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Buy & Sell Gold']],
            value: ApprovalTabsEnum['Buy & Sell Gold'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.BuySellGold),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Inventory']],
            value: ApprovalTabsEnum['Inventory'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.Inventory),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Discount']],
            value: ApprovalTabsEnum['Discount'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.Discount),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Product Management']],
            value: ApprovalTabsEnum['Product Management'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.Product),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Collateral']],
            value: ApprovalTabsEnum['Collateral'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.CustomerManagement),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Project Management']],
            value: ApprovalTabsEnum['Project Management'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.Project),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Transfers']],
            value: ApprovalTabsEnum['Transfers'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.InventoryTransfer),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Release Earmark']],
            value: ApprovalTabsEnum['Release Earmark'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.BuySellGold),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Branch']],
            value: ApprovalTabsEnum['Branch'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.BranchManagement),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Conversion']],
            value: ApprovalTabsEnum['Conversion'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.Conversion),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Earmark Conversion']],
            value: ApprovalTabsEnum['Earmark Conversion'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.Conversion),
        },
        {
            label: ApprovalTabsEnum[ApprovalTabsEnum['Stock Out']],
            value: ApprovalTabsEnum['Stock Out'],
            enabled: !!viewAccessModules.find(module => module === ModuleEnum.StockOut || module === ModuleEnum.Conversion),
        },
    ];

    const tabValues = availableTabValues
        .filter(tab => tab.enabled)
        .map(({ enabled, ...tab }) => tab);

    const tabs = [
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Buy & Sell Gold']],
            element: <BuyAndSellGoldApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Inventory']],
            element: <GoldSilverInventoryApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Discount']],
            element: <DiscountApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Product Management']],
            element: <ProductApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Collateral']],
            element: <CollateralApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Project Management']],
            element: <ProjectApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Transfers']],
            element: <TransfersApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Release Earmark']],
            element: <ReleaseEarmarkApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Branch']],
            element: <BranchesApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Conversion']],
            element: <GoldConversionApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Earmark Conversion']],
            element: <EarmarkApproval />,
        },
        {
            name: ApprovalTabsEnum[ApprovalTabsEnum['Stock Out']],
            element: <StockOutApproval />,
        },
    ];

    return (
        <TabSelector
            tabs={tabValues}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
        >
            <TabDisplay
                tabs={tabs}
                currentTab={ApprovalTabsEnum[selectedTab]}
                containerStyle={Styles['tab-display']}
            />
        </TabSelector>
    );
};

export default Settings;
